import React from 'react';
import type { FC } from 'react';
import { useTranslation } from '@wix/cli-i18n';
import kebabCase from 'lodash/kebabCase.js';
import { ConfirmInput, SelectInput, TextInput } from '@wix/cli-ui-kit';
import type { SelectOption } from '@wix/cli-ui-kit';
import { useProjectModel } from '../../../model.js';
import { useWizardStep } from '../Wizard.js';
import {
  createDashboardRouteValidation,
  useValidation,
} from '../../../dev-center/components/validations/index.js';
import { useExtensions } from '../../extensions/useExtensions.js';
import { useWixCliAppBi } from '../../../bi/index.js';

export const TitleStep: FC<{
  pageTitle: string | undefined;
  onSubmit: (value: string) => void;
}> = ({ onSubmit, pageTitle }) => {
  const { t } = useTranslation();
  const bi = useWixCliAppBi();
  const extensionType = t('generate_command.flow.dashboard_page').toLowerCase();
  const { isActive, goToNextStep } = useWizardStep();

  return (
    <TextInput
      isActive={isActive}
      label={t('generate_command.dashboard_page.choose_title')}
      placeholder={
        pageTitle ?? t('generate_command.dashboard_page.title_placeholder')
      }
      onSubmit={(pageTitle) => {
        bi.cliFlowStepAnswered({
          question: t('generate_command.dashboard_page.choose_title'),
          questionKey: 'generate_command.dashboard_page.choose_title',
          answer: pageTitle,
          additionalInfo: { extensionType },
        });
        onSubmit(pageTitle);
        goToNextStep();
      }}
    />
  );
};

export const RouteStep: FC<{
  pageRoute: string | undefined;
  pageTitle: string | undefined;
  onSubmit: (value: string) => void;
}> = ({ pageRoute, pageTitle, onSubmit }) => {
  const { t } = useTranslation();
  const bi = useWixCliAppBi();
  const extensionType = t('generate_command.flow.dashboard_page').toLowerCase();
  const defaultPageRoute = kebabCase(pageTitle);
  const { getExtensions } = useExtensions();
  const dashboardPages = getExtensions('DashboardPage');
  const validateDashboardRoute = useValidation(
    createDashboardRouteValidation(dashboardPages)
  );
  const { isActive, goToNextStep } = useWizardStep();

  return (
    <TextInput
      isActive={isActive}
      label={t('generate_command.dashboard_page.choose_route')}
      placeholder={pageRoute ?? defaultPageRoute}
      onSubmit={(value) => {
        bi.cliFlowStepAnswered({
          question: t('generate_command.dashboard_page.choose_route'),
          questionKey: 'generate_command.dashboard_page.choose_route',
          answer: value,
          additionalInfo: { extensionType },
        });
        onSubmit(value);
        goToNextStep();
      }}
      validate={validateDashboardRoute}
    />
  );
};

export const AddToCategoryStep: FC<{
  value: boolean | undefined;
  onSubmit: (value: boolean) => void;
}> = ({ value, onSubmit }) => {
  const { t } = useTranslation();
  const bi = useWixCliAppBi();
  const extensionType = t('generate_command.flow.dashboard_page').toLowerCase();
  const { isActive, goToNextStep } = useWizardStep();

  return (
    <ConfirmInput
      initialValue={value}
      isActive={isActive}
      label={t('generate_command.dashboard_page.add_to_category')}
      onSubmit={(value) => {
        bi.cliFlowStepAnswered({
          question: t('generate_command.dashboard_page.add_to_category'),
          questionKey: 'generate_command.dashboard_page.add_to_category',
          answer: String(value),
          additionalInfo: { extensionType },
        });
        onSubmit(value);
        goToNextStep();
      }}
    />
  );
};

export const ChooseCategoryStep: FC<{ onSubmit: (value: string) => void }> = ({
  onSubmit,
}) => {
  const { t } = useTranslation();
  const bi = useWixCliAppBi();
  const extensionType = t('generate_command.flow.dashboard_page').toLowerCase();
  const { model } = useProjectModel();
  const { isActive, goToNextStep } = useWizardStep();

  const categories = model.config.dashboard?.sidebar?.categories ?? [];
  const categoriesOptions: Array<SelectOption<string>> = categories.map(
    ({ id, label }) => ({
      title: label,
      value: id,
    })
  );

  return (
    <SelectInput<string>
      label={t('generate_command.dashboard_page.choose_category')}
      options={categoriesOptions}
      isActive={isActive}
      onSubmit={(option) => {
        bi.cliFlowStepAnswered({
          question: t('generate_command.dashboard_page.choose_category'),
          questionKey: 'generate_command.dashboard_page.choose_category',
          answer: option.title,
          additionalInfo: { extensionType },
        });
        onSubmit(option.value);
        goToNextStep();
      }}
    />
  );
};
